// PROFIT SHARE, rebuilt (UI rebuild batch 3; drawn when lib/ui-v2.ts switches batch 3 on, inside Record's page).
//
// Astra's form, the operator's ruling of 26 Oct 2026 ("simplified or closer to that version"):
//   card    Party | Asset, Amount — the share's constraint (shareProblem) in one line right under the amount, and on the
//           phone what the desk holds of the party under its name; "More" (As of, Notes); the foot: "Review once before
//           confirming." and the one gold primary, Review share ("Balance not read" while the balance is unknown).
//           Reviewed, the foot becomes the review — off whom, the balance before → after, what comes off the sub-broker
//           cost — with Edit and the confirm that posts.
//   aside   laptop only: Held for party (the box in the Books' words), Desk and when it was read, About this job.
//
// THE STATE MACHINE IS screens/RecordProfitShare.tsx's, COPIED VERBATIM: the party pick (own accounts excluded),
// readState for the names and the balances, the balance unknown = null (never a zero), shareProblem (imported — the same
// refusal the BFF gives, earlier), `unchecked` keeping the post off until a read confirms the balance, the reference
// minted once per entry and held across a retry, refreshMoneyViews on success, the read-back from what was POSTED
// (PostedLine, deduped said as "already recorded"). This route has no repeat guard (bff routes/expenses.ts /profit-share),
// so there is no RepeatPanel and no confirm_repeat — none is added. Only the layout changed, plus Astra's review step,
// which adds a tap and refuses nothing.
import { useState, useMemo, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { api, refreshMoneyViews } from '../../../lib/api';
import { tg } from '../../../lib/telegram';
import { ASSETS, makeReference } from '../../../lib/workflow';
import { amountOk, amountProblem } from '../../../lib/amount';
import { isSentinel, nothingLanded, readWords } from '../../../lib/errors';
import { Combobox, type ComboboxOption } from '../../Combobox';
import { rosterCut, usePartySearch } from '../../../lib/roster-cut';
import { AsOfChips } from '../../AsOfChips';
import { Field } from '../../Field';
import { boxParts } from '../../BoxLines';
import { BalanceRows } from './BalanceRows';
import { ErrorLine } from '../../ErrorLine';
import { MoreDetails } from '../../MoreDetails';
import { StateLine, ServerLine } from '../kit';
import { Receipt, ReceiptAmount, postedAsOf } from '../Receipt';
import { shareProblem, type Posted } from '../../../screens/RecordProfitShare';
import {
  partyName, partySubtitle, isStale, needsNameReview, rankForQuery, foldAsset, partyBoxWords, partyBalanceOf,
} from '../../../lib/naming';
import { displayAsset, fmtAmount, fmtDay, dubaiWallToIso, backdateProblem } from '../../../lib/format';
import { readState } from '../../../lib/read-state';
import { AsideHead, AssetSelect, FormFoot, FormLayout, ReviewBlock, deskFacts } from './p2p/parts';
import { AboutFold } from './parts';

/** The assets on offer, FOLDED, without USDC (the party's box folds the dollar stables into one USDT line). */
const SHARE_ASSETS: string[] = [...new Set(ASSETS.map((a) => foldAsset(a)))].filter((a) => a !== 'USDC');

export function ProfitShareV2() {
  const [slug, setSlug]     = useState('');
  const [asset, setAsset]   = useState<string>('INR');
  const [amount, setAmount] = useState('');
  const [notes, setNotes]   = useState('');

  // ── WHEN DID THIS HAPPEN? Empty means now. Dubai wall-clock, read through lib/format.ts.
  const [when, setWhen] = useState('');
  const whenIso   = when ? dubaiWallToIso(when) : '';
  const whenWords = backdateProblem(when);

  // ── the party — the same picker as the Party mode, the same exclusions ────
  const parties = usePartySearch({
    base: ['parties', 'dubaiotc'], text: slug,
    ask: (q) => api.listParties({ entity: 'dubaiotc', q, with_balances: true }),
    enabled: slug.length > 0,
  });
  const rows = parties.data?.parties ?? [];
  // WHAT THE LIST IS WORTH — readState (lib/read-state.ts; Appendix B): unknown is never what it last said and never a zero.
  const lookup = readState(parties);
  const listBalances = readState(parties, { balances: true });
  const balancesRead = listBalances === 'known';
  const partyOptions: ComboboxOption[] = useMemo(() =>
    // OWN ACCOUNTS ARE NOT PARTIES HERE: a profit share comes off a party's balance, and a desk account has none.
    rankForQuery(rows.filter((p) => p.party_type !== 'own_account'), slug).map((p) => ({
      value:     p.slug,
      label:     partyName(p),
      secondary: [partySubtitle(p, { compact: true }), balancesRead ? partyBoxWords(p) : ''].filter(Boolean).join(' · '),
      stale:     isStale(p),
      review:    needsNameReview(p.display_name),
    }))
  , [parties.data, balancesRead, slug]);   // eslint-disable-line react-hooks/exhaustive-deps
  const selected = rows.find((p) => p.slug === slug && p.party_type !== 'own_account') ?? null;
  const name = selected ? partyName(selected) : slug;

  // ── WHAT THE DESK HOLDS, before and after ─────────────────────────────────
  const amountN     = Number(amount) || 0;
  const amountWords = amountProblem(amount);
  const balanceNow: number | null = selected && balancesRead ? partyBalanceOf(selected.balances, asset) : null;   // null = unknown
  const held        = balanceNow !== null && balanceNow < 0 ? -balanceNow : 0;      // what the desk holds of theirs
  const balanceNext = balanceNow !== null ? balanceNow + amountN : null;            // the share comes OFF the box
  // THE RULE, before the tap: with the figure unknown there is nothing to refuse FROM — and nothing to approve from
  // either, so the post stays off (`unchecked`) until a read confirms it.
  const shareWords = balanceNow !== null && balanceNext !== null ? shareProblem(held, amountN, balanceNext, asset) : null;
  const unchecked = !!selected && balanceNow === null;

  // ── submit ────────────────────────────────────────────────────────────────
  // THE REFERENCE IS THE IDEMPOTENCY KEY, SO IT MUST SURVIVE A RETRY — minted once per entry, held in a ref, cleared on
  // success, retired when the day it carries would change and the books provably hold nothing under it.
  const refRef = useRef<string | null>(null);
  const [posted, setPosted] = useState<Posted | null>(null);

  const post = async () => {
    refRef.current ??= makeReference('PSH', whenIso || undefined);
    const reference = refRef.current;
    const data = await api.profitShare({
      entity:       'dubaiotc',
      party_slug:   slug,
      amount:       { asset, amount: amountN },
      reference,
      notes:        notes.trim() || undefined,
      effective_at: whenIso || undefined,
    });
    return { data, reference, asOf: whenIso || null, amount, asset, name };
  };
  const qc = useQueryClient();
  const submit = useMutation({
    mutationFn: post,
    onSuccess: ({ data, reference, asOf, amount, asset, name }) => {
      refRef.current = null;
      // The box on screen must show the share taken (reviewer, 17 Sept 2026).
      refreshMoneyViews(qc);
      setPosted({ reference: data.reference ?? reference, asOf, deduped: !!data.deduped, amount, asset, name });
      // The figures leave with the entry they were typed for; the party stays.
      setWhen(''); setAmount(''); setNotes('');
      tg()?.HapticFeedback.notificationOccurred('success');
    },
    onError: (e) => { if (!isSentinel(e)) tg()?.HapticFeedback.notificationOccurred('error'); },
  });
  const retireReference = () => {
    const mayHold = submit.isPending || (submit.isError && !nothingLanded(submit.error));
    if (!mayHold) refRef.current = null;
  };

  const canPost = !submit.isPending && !!selected && !unchecked && amountOk(amount) && !shareWords && !whenWords;
  // Why Review is off, in a few words (Record's rule; Screens' audit, 27 Oct 2026). The share's own check and an unread
  // balance say their words on the form and on the button.
  const missing = !selected ? 'Pick a party.'
    : !amountOk(amount) ? 'Enter an amount.'
    : whenWords ? 'Check the date.'
    : null;

  // ── THE REVIEW (Astra: "Review share", then the confirm). It holds only for the entry it was opened on — any change
  // to a field, or a re-read balance, closes it — and refuses nothing: the confirm posts under the same canPost.
  const entryKey = JSON.stringify([slug, asset, amount, when, notes, balanceNow]);
  const [reviewedKey, setReviewedKey] = useState<string | null>(null);
  const reviewing = reviewedKey === entryKey;
  const asOfWords = whenIso && !whenWords ? ` as of ${fmtDay(whenIso)}` : '';
  const readAt = balancesRead && parties.dataUpdatedAt ? parties.dataUpdatedAt : null;

  // About this job: the explanation, the desk and when the balances were read — beside the form, and under it on the phone.
  const about = (className = '') => (
    <AboutFold className={className} facts={deskFacts(readAt)} help={<>
      <p>The desk’s share of a sub-broker’s balance: the party’s balance falls by the amount and the sub-broker cost falls by the same, so profit rises.</p>
      <p>No cash moves and no desk account is touched — the cash half is recorded as Money out on its own.</p>
    </>} />
  );

  const card = (
    <>
      <div className="grid grid-cols-2 gap-3 lg:gap-4">
        <Field label="Party" className="col-span-2 lg:col-span-1">
          <Combobox
            value={slug}
            onChange={setSlug}
            options={partyOptions}
            more={rosterCut(parties.data)}
            loading={slug.length > 0 && lookup === 'waiting'}
            placeholder="Search party"
            emptyHint={slug.length === 0 ? null : lookup === 'failed' ? 'Parties could not be read.' : undefined}
          />
          {/* On the phone (no aside): what the desk holds, in the Books' words, kept with the party. */}
          {balanceNow !== null && <BalanceRows className="xl:hidden" lines={[{ asset, now: balanceNow }]} />}
        </Field>
        <Field label="Asset">
          <AssetSelect label="Asset" value={asset} onChange={setAsset} options={SHARE_ASSETS} />
        </Field>
        <Field label="Amount">
          <input aria-label={`Amount in ${displayAsset(asset)}`} className="input" value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" />
          <ErrorLine>{amountWords}</ErrorLine>
          <ErrorLine>{shareWords}</ErrorLine>
        </Field>
      </div>
      {listBalances === 'failed' && (
        <StateLine tone="warn" more={lookup === 'failed' ? readWords(parties.error) : undefined} action={<button type="button" className="btn-link text-meta" onClick={() => void parties.refetch()}>Retry</button>}>
          {lookup === 'failed' ? 'Couldn’t read parties — try again.' : 'Balances not read — try again.'}
        </StateLine>
      )}

      <div className="border-t border-stroke-soft pt-3">
        <MoreDetails label="More">
          {/* The reference carries the day — a new day, a new reference, unless the old one may already be in the ledger. */}
          <AsOfChips value={when} onChange={(w) => { setWhen(w); retireReference(); }} disabled={submit.isPending} />
          <Field label="Notes">
            <textarea className="input h-16" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything to remember" />
          </Field>
        </MoreDetails>
      </div>

      {reviewing && balanceNow !== null && balanceNext !== null ? (
        <ReviewBlock>
          <p className="text-body">
            <ReceiptAmount value={amountN} asset={asset} /> off <span className="break-words">{name}</span>{asOfWords}
          </p>
          <BalanceRows lines={[{ asset, now: balanceNow, after: balanceNext }]} />
          <p className="text-meta text-tg-hint">
            <span className="whitespace-nowrap">{fmtAmount(amountN, asset)} {displayAsset(asset)}</span> comes off the sub-broker cost.
          </p>
          <div className="flex flex-col gap-2 pt-2 lg:flex-row lg:justify-end">
            <button type="button" className="btn-secondary min-h-[44px] lg:min-h-[40px] lg:px-4" onClick={() => setReviewedKey(null)}>Edit</button>
            <button
              className="btn-primary w-full lg:w-auto lg:px-6"
              disabled={!canPost}
              onClick={() => { if (canPost) submit.mutate(); }}
            >
              {submit.isPending ? 'Booking…' : `Record profit share${asOfWords}`}
            </button>
          </div>
        </ReviewBlock>
      ) : (
        <FormFoot line={missing && !submit.isPending ? missing : null}>
          <button
            className="btn-primary w-full lg:w-auto lg:px-6"
            disabled={!canPost}
            onClick={() => { if (canPost) setReviewedKey(entryKey); }}
          >
            {/* The only thing between the operator and the button is a balance nobody could read: say so on the button. */}
            {unchecked && amountOk(amount) && !whenWords ? 'Balance not read'
              : <>Review share<ArrowRight size={16} className="ml-2 inline" aria-hidden /></>}
          </button>
        </FormFoot>
      )}

      {submit.isError && !isSentinel(submit.error) && <ServerLine error={submit.error} />}
      {about('xl:hidden')}
    </>
  );

  const aside = (
    <>
      <AsideHead
        title="Held for party"
        // The figure alone in the figure's place; its words on the line under it (one fact per place, 1 Nov 2026).
        figure={balanceNow === null ? '—' : boxParts(balanceNow, asset).figure
          ? <span className={balanceNow < 0 ? 'text-neg' : 'text-tg-text'}>{boxParts(balanceNow, asset).figure}<span className="text-meta text-tg-hint">{`\u00a0${displayAsset(asset)}`}</span></span>
          : 'Square'}
        line={balanceNow !== null && boxParts(balanceNow, asset).figure ? (balanceNow > 0 ? 'Owes you' : 'You hold') : undefined}
      />
      {about()}
    </>
  );

  /* THE RECEIPT IN THE FORM'S PLACE (his "screen UI and confirmation msgs!!!!", 1 Nov 2026): what POSTED. */
  const receipt = submit.isSuccess && posted ? (
    <Receipt
      title="Profit share recorded"
      deduped={posted.deduped}
      reference={posted.reference}
      facts={[
        { label: 'Share', value: <ReceiptAmount value={posted.amount} asset={posted.asset} /> },
        { label: 'Off the balance of', value: posted.name },
        { label: 'As of', value: postedAsOf(posted.asOf, fmtDay, submit.submittedAt) },
      ]}
      another={{ label: 'Record another', onClick: () => submit.reset() }}
    />
  ) : null;

  // After a posting the receipt says it all; the pre-confirmation aside goes (Astra, 1 Nov 2026).
  return <FormLayout card={receipt ?? card} aside={receipt ? null : aside} />;
}
